import {
  type FC,
  type ReactNode,
  useState,
  type SetStateAction,
  type Dispatch,
} from 'react';
import * as styles from './DropdownMenu.css';
import IconRow from '../../app/JobSection/JobActionMobile/IconRow';
import { Box, Stack } from 'braid-design-system';
import buildHandleEnterAndSpaceKeyPress, {
  linkPropsWithKeyboardNavigation,
} from '../../../utils/buildHandleEnterAndSpaceKeyPress';
import { observer } from 'mobx-react';
import { JobAction } from 'src/components/app/JobSection/actions';

export interface Option {
  label: string;
  value: string;
  disabled?: boolean;
  disableDescription?: ReactNode;
  content?: ReactNode;
  icon?: ReactNode;
}

interface Props {
  checked: boolean;
  setChecked: Dispatch<SetStateAction<boolean>>;
  children: ReactNode;
  options: Option[];
  onClick: (value: string) => void;
  width?: string;
  checkboxId: string;
  useLabel?: boolean;
}

// A workaround, where the value of the key was impacting the aria-label,
// possibly due to the options being updated after render.
// There is a future task to utilise the BRAID dropdown, which
// will hopefully fix this issue.
const randomKeyGenerator = () =>
  Math.floor(Math.random() * Math.floor(10000000000));

const DropdownMenu: FC<Props> = ({
  checked,
  setChecked,
  children,
  options,
  onClick,
  width = 'auto',
  checkboxId,
  useLabel = false,
  ...restProps
}) => {
  const [id] = useState(checkboxId);

  const toggleChecked = () => {
    setChecked(!checked);
  };

  const createValueChanged = (value: string) => () => {
    if (value === JobAction.SHARE) {
      return;
    }
    onClick(value);
    toggleChecked();
  };

  const CtaToRender = () => {
    if (useLabel) {
      return (
        <label
          {...restProps}
          id="menu-label"
          htmlFor={id}
          data-testid="dropdown-label"
          className={styles.label}
          tabIndex={0}
          onKeyDown={buildHandleEnterAndSpaceKeyPress(toggleChecked)}
        >
          {children}
        </label>
      );
    }

    return <div onClick={() => toggleChecked()}>{children}</div>;
  };
  return (
    <div
      aria-labelledby="menu-label"
      aria-describedby="menu-label"
      id={`content-${checkboxId}`}
      className={styles.root}
    >
      <div>
        <input
          data-testid="dropdown-item"
          aria-hidden="true"
          type="checkbox"
          id={id}
          className={styles.checkbox}
          checked={checked}
          onChange={() => toggleChecked()}
          tabIndex={-1}
        />

        <CtaToRender />
        <label htmlFor={id} className={styles.backdrop}>
          Done
        </label>

        <Box
          marginTop="small"
          role="navigation"
          className={styles.options}
          zIndex="dropdown"
          style={{ width }}
          data-testid="dropdown-menu"
        >
          <Stack space="none">
            {options.map(
              ({
                value,
                disabled,
                disableDescription,
                label: optionLabel,
                icon,
                content,
              }) => (
                <Box
                  key={randomKeyGenerator()}
                  width="full"
                  paddingX="small"
                  paddingY="small"
                  aria-label={value}
                  className={{
                    [styles.option]: value !== JobAction.SHARE,
                    [styles.optionDisabled]: disabled,
                  }}
                  {...linkPropsWithKeyboardNavigation(
                    createValueChanged(value),
                  )}
                >
                  <IconRow
                    key={value}
                    icon={icon}
                    label={optionLabel}
                    onClick={() => createValueChanged(value)}
                    disabled={disabled}
                    disableDescription={disableDescription}
                    content={content}
                  />
                </Box>
              ),
            )}
          </Stack>
        </Box>
      </div>
    </div>
  );
};

export default observer(DropdownMenu);
